import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'

interface FaqSectionProps {
    faqs: Faq[]
    label?: string
}

const FaqSection: FC<FaqSectionProps> = ({ faqs, label = 'Questions' }): ReactNode => {
    const [open, setOpen] = useState<string | null>(faqs[0]?.id ?? null)

    if (!faqs.length) return null

    return (
        <section id='faq' aria-labelledby='faq-title' data-nav-theme='light' className='bg-cream text-charcoal'>
            <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                <div data-reveal className='lg:col-span-4'>
                    <SectionLabel>{label}</SectionLabel>
                    <h2 id='faq-title' className='mt-6 font-heading type-section'>
                        Good questions.
                    </h2>
                    <p className='mt-6 max-w-xs text-[16px] leading-relaxed text-charcoal/80'>
                        The things clients usually ask before we start working together.
                    </p>
                </div>

                <ul className='border-t border-charcoal lg:col-span-8'>
                    {faqs.map((faq, index) => {
                        const expanded = open === faq.id
                        const panelId = `faq-panel-${faq.id}`
                        const buttonId = `faq-button-${faq.id}`

                        return (
                            <li key={faq.id} data-reveal className='border-b border-charcoal/25'>
                                <h3>
                                    <button
                                        id={buttonId}
                                        type='button'
                                        aria-expanded={expanded}
                                        aria-controls={panelId}
                                        onClick={() => setOpen(expanded ? null : faq.id)}
                                        className='group grid w-full grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-6 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral md:py-7'
                                    >
                                        <span className='font-mono text-xs text-charcoal/75'>{pad(index + 1)}</span>
                                        <span className='font-heading font-expanded text-lg font-bold leading-snug tracking-[-0.01em] md:text-xl'>
                                            {faq.question}
                                        </span>
                                        <span
                                            className={cn(
                                                'flex size-10 items-center justify-center rounded-sm border transition-all duration-300',
                                                expanded ? 'rotate-45 border-coral bg-coral' : 'border-charcoal/30 group-hover:border-charcoal'
                                            )}
                                        >
                                            <Plus aria-hidden='true' className='size-5' />
                                        </span>
                                    </button>
                                </h3>
                                <div
                                    id={panelId}
                                    role='region'
                                    aria-labelledby={buttonId}
                                    className={cn('grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]', expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                                >
                                    <div className='min-h-0 overflow-hidden' inert={!expanded}>
                                        <p className='max-w-2xl pb-7 pl-[3.5rem] text-[16px] leading-relaxed text-charcoal/80'>
                                            {faq.answer}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default FaqSection
